import React from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import ChatAvatar from './ChatAvatar';
import type { ChatRoom } from '../../types/chat';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';
import { parseApiDate } from '../../utils/apiDates';

interface RoomListItemProps {
  room: ChatRoom;
  active?: boolean;
  onClick?: () => void;
}

export function deriveRoomDisplay(
  room: ChatRoom,
  selfId: string | null
): { name: string; avatar: string | null; online: boolean } {
  if (room.type === 'group') {
    return {
      name: room.name ?? 'Group chat',
      avatar: room.icon_url,
      online: false,
    };
  }
  // Direct: prefer the backend-computed counterpart label (set from the
  // caller's JWT), fall back to picking the other member locally.
  const selfUpper = (selfId ?? '').toUpperCase();
  const peer =
    room.members.find((m) => (m.user_id ?? '').toUpperCase() !== selfUpper) ??
    room.members[0];
  return {
    name:
      room.display_name ?? peer?.full_name ?? peer?.email ?? 'Direct message',
    avatar: room.display_avatar ?? peer?.avatar_url ?? null,
    online: !!peer?.is_online,
  };
}

const RoomListItem: React.FC<RoomListItemProps> = ({ room, active, onClick }) => {
  const { userId } = useCurrentChatIdentity();
  const display = deriveRoomDisplay(room, userId);
  const time = room.last_message_at
    ? formatDistanceToNowStrict(parseApiDate(room.last_message_at), { addSuffix: false })
    : '';
  const preview = room.last_message_preview ?? '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-full text-left transition-colors ${
        active
          ? 'bg-neutral-100 dark:bg-neutral-800/60'
          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/30'
      }`}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-7 rounded-r-full bg-primary-500"
        />
      )}
      <ChatAvatar src={display.avatar} name={display.name} size={40} online={display.online} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold text-text-primary">{display.name}</p>
          {time && <span className="text-[11px] text-text-tertiary shrink-0">{time}</span>}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="truncate text-xs text-text-secondary">{preview}</p>
          {room.unread_count > 0 && (
            <span className="shrink-0 min-w-[18px] h-[18px] flex items-center justify-center bg-error-600 text-white text-[10px] font-bold rounded-full px-1">
              {room.unread_count > 99 ? '99+' : room.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default RoomListItem;
